import React, { useState, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { couponViews } from './CouponContainer.utils';
import CouponDialog from '../CouponDialog/CouponDialog';
import FloatingActionButton from '../FloatingActionButton/FloatingActionButton';
import { trackEvent } from '../../util/track';
import { getCookieDomain } from '../../util/util';
import { LOOP_ONSTORE_COOKIE } from '../../util/loopReturns';
import RichTextPropTypes from '../../prop-types/rich-text';
import { isBrowser } from '../../util/ssr';
import { decide } from '../../util/optimizely';
import { GET_USER_LOCALE } from '../App.gql';

/**
 * CouponContainer displays a CouponDialog, a Floating Action Button, or nothing based on cookies.
 * This component will display nothing on excluded pages.
 */
const CouponContainer = ({
  isAutoApplied,
  couponCode,
  smsTermsOfService,
  codeDiscountPercentage,
  viewToDisplay,
  setViewToDisplay
}) => {
  const TEN_YEARS = 365 * 10;
  const { MODAL, BUTTON, NONE } = couponViews;

  const excludedPaths = new RegExp(
    [
      '/pages/on-the-move',
      '/pages/sticky',
      '/pages/the-skimm-december',
      '/pages/sxm?utm_source=sxm&utm_medium=siriusxm',
      '/pages/quiz',
      'podcast',
      'sirius',
      'radio',
      '/checkouts/',
      '/collections/paypal',
      '/collections/kpth',
      '/collections/performline',
      '/collections/exclusive25',
      '/collections/womens-exclusive25',
      '/collections/mens-exclusive25',
      '/collections/bombas-x-strava',
      '/collections/womens-bombas-x-strava',
      '/collections/mens-bombas-x-strava',
      '/find-a-store',
      '/orders/',
      '/pages/giving-directory',
      '/pages/help',
      '/preferences',
      '/pages/contact-us',
      '/pages/find-a-store',
      '/pages/page-status',
      '/page-status',
      '/account',
      '/pages/privacy-policy',
      '/pages/bombas-terms-and-conditions-of-use',
      'beebetter.bombas.com'
    ].join('|'),
    'i'
  );

  const excludedCookies = [
    'bounceClientVisit1156c',
    'bounceClientVisit1156v',
    'BOM-CAT',
    'discount_code',
    LOOP_ONSTORE_COOKIE
  ];

  const urlParams = new URLSearchParams(
    isBrowser() ? window.location.search : ''
  );

  const [showModalOnInteraction, setShowModalOnInteraction] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const { data } = useQuery(GET_USER_LOCALE);
  const country = data?.user?.locale?.country;

  const displayModal = showModalOnInteraction
    ? viewToDisplay === MODAL && hasUserInteracted
    : viewToDisplay === MODAL;

  const rejectedOfferStatus = sessionStorage.getItem(
    'returningUserOfferButton'
  );

  const hasSeenCouponDialog = useMemo(
    () => !!Cookies.get('optimizelyNewCustomerOfferEmailCaptureImpression'),
    []
  );

  const hasSubmittedEmailOptIn = !!Cookies.get(
    'optimizelyNewCustomerOfferEmailCaptureSuccess'
  );

  const isExcludedPage = excludedPaths.test(window.location);
  const hasExcludedCookie = Object.keys(Cookies.get()).some((key) =>
    excludedCookies.includes(key)
  );

  useEffect(() => {
    // Expose display of couponDialog to window (TEMP: Used by CRO Metrics)
    if (window.Bombas) {
      window.Bombas.couponDialog = { display: () => setViewToDisplay(MODAL) };
    }

    trackEvent(
      {
        event: 'croTest',
        expName: 'Optimizely - New Customer Offer Email Capture',
        expGroup: 'Impression',
        expVariation: '',
        eventNonInteraction: 1
      },
      {
        // Reset eventNonInteraction
        eventNonInteraction: 0
      }
    );
  }, []);

  useEffect(() => {
    async function setContainerView() {
      if (
        isExcludedPage ||
        hasExcludedCookie ||
        hasSubmittedEmailOptIn ||
        rejectedOfferStatus
      ) {
        return;
      }

      if (
        (hasSeenCouponDialog &&
          !hasSubmittedEmailOptIn &&
          !rejectedOfferStatus) ||
        country !== 'US'
      ) {
        setViewToDisplay(BUTTON);
        return;
      }

      let enableShowOnInteraction = false;
      if (!hasSeenCouponDialog) {
        const forcedShowOnInteraction =
          urlParams && urlParams.get('coupon_modal_changes');
        if (forcedShowOnInteraction) {
          enableShowOnInteraction = true;
        } else {
          const featureDecision = await decide('coupon_modal_updates');
          const isUpdateEnabled = featureDecision?.variationKey === 'test';
          enableShowOnInteraction = !!isUpdateEnabled;
        }

        setShowModalOnInteraction(enableShowOnInteraction);

        if (enableShowOnInteraction) {
          setViewToDisplay(MODAL);
        } else {
          setTimeout(() => setViewToDisplay(MODAL), 3000);
          Cookies.set(
            'optimizelyNewCustomerOfferEmailCaptureImpression',
            true,
            {
              domain: getCookieDomain(),
              expires: TEN_YEARS
            }
          );
        }
      }
    }
    setContainerView();
  }, []);

  useEffect(() => {
    const handleInteraction = () => {
      if (!hasUserInteracted) {
        setHasUserInteracted(true);
      }
    };

    if (showModalOnInteraction) {
      window.addEventListener('scroll', handleInteraction, { once: true });
      window.addEventListener('keydown', handleInteraction, { once: true });
      window.addEventListener('touchstart', handleInteraction, { once: true });
      window.addEventListener('mousemove', handleInteraction, { once: true });
    }

    return () => {
      if (showModalOnInteraction) {
        window.removeEventListener('scroll', handleInteraction);
        window.removeEventListener('keydown', handleInteraction);
        window.removeEventListener('touchstart', handleInteraction);
        window.removeEventListener('mousemove', handleInteraction);
      }
    };
  }, [showModalOnInteraction]);

  useEffect(() => {
    if (displayModal) {
      Cookies.set('optimizelyNewCustomerOfferEmailCaptureImpression', true, {
        domain: getCookieDomain(),
        expires: TEN_YEARS
      });
      trackEvent({ event: 'couponModalImpression' });
    }
  }, [displayModal]);

  return (
    <>
      <CouponDialog
        couponCode={couponCode}
        discountPercentage={codeDiscountPercentage}
        isAutoApplied={isAutoApplied}
        isDisplayed={displayModal}
        handleClose={() => {
          trackEvent({ event: 'couponModalClose' });
          setViewToDisplay(NONE);
          if (
            Cookies.get('optimizelyNewCustomerOfferEmailCaptureImpression') &&
            !Cookies.get('optimizelyNewCustomerOfferEmailCaptureSuccess') &&
            !rejectedOfferStatus
          ) {
            setTimeout(() => setViewToDisplay(BUTTON), 100);
          }
        }}
        smsTermsOfService={smsTermsOfService}
      />
      <FloatingActionButton
        title={`Get ${codeDiscountPercentage}% Off`}
        isDisplayed={viewToDisplay === BUTTON}
        onDisplay={() => {
          trackEvent({ event: 'Coupon Bubble View' });
        }}
        onConfirm={() => {
          trackEvent({ event: 'Coupon Bubble Click' });
          setTimeout(() => setViewToDisplay(MODAL, 100));
        }}
        onReject={() => {
          trackEvent({ event: 'Coupon Bubble Close' });
          sessionStorage.setItem('returningUserOfferButton', true);
          setViewToDisplay(NONE);
        }}
      />
    </>
  );
};

CouponContainer.propTypes = {
  /**
   * Coupon code to auto apply
   */
  couponCode: PropTypes.string,
  /**
   * If `true`, apply coupon code
   */
  isAutoApplied: PropTypes.bool,
  /**
   * Contentful object, Settings->RichTextResource for SMS Legal Copy
   */
  smsTermsOfService: PropTypes.shape(RichTextPropTypes),
  /**
   * Discount code percentage set in Contentful, should match percentage value set for that coupon code in Shopify
   */
  codeDiscountPercentage: PropTypes.number,
  /**
   * View to display (MODAL, BUTTON, NONE)
   */
  viewToDisplay: PropTypes.oneOf(['hidden', 'button', 'modal']).isRequired,
  /**
   * Function to set the view to display
   */
  setViewToDisplay: PropTypes.func.isRequired
};

CouponContainer.defaultProps = {
  couponCode: '',
  isAutoApplied: false,
  codeDiscountPercentage: 20
};

export default CouponContainer;
